import type { Prayer, PrayerStatus } from '@/types/prayer';
import i18n from '@/i18n';
import { tokenize } from '@/lib/search-prayers';
import { canonicalizeWho, getWhoMergeMap } from '@/lib/settings-store';
import {
  GENERAL_KEY,
  isSelf,
  whoKey,
  levenshtein,
  couldBeSamePerson,
  isContainmentMatch,
  isAliasCandidateEligible,
  whoOfWithMap,
  peopleInPrayerWithMap,
} from '@/lib/who-match';

// These identity/alias helpers moved to the probe-clean `who-match` leaf in
// P0 (docs/plans/people-alias-suggestion-audit-2026-06-08.md) so a `tsx`
// probe can load them. Re-exported from here so the app's existing import
// sites keep importing them from `prayer-utils`. `aliasPairKey` is
// load-bearing: Garden.tsx imports it from this module.
export {
  SELF_KEY,
  GENERAL_KEY,
  isSelf,
  isPraise,
  couldBeSamePerson,
  aliasPairKey,
} from '@/lib/who-match';

// Live-locale tag for Intl.DateTimeFormat. Reading at format time (not at
// module load) lets the user switch language mid-session and have every
// timestamp re-render correctly. `i18n.language` is always populated by
// the LanguageDetector before any formatter runs.
export function canonicalLocale(language = i18n.language): 'en-US' | 'es-MX' | 'pt-BR' {
  const base = (language || 'en').slice(0, 2).toLowerCase();
  if (base === 'es') return 'es-MX';
  if (base === 'pt') return 'pt-BR';
  return 'en-US';
}

function formatLocale(): string {
  return canonicalLocale();
}

export type Tier = 'seedling' | 'growing' | 'flourishing';
export type NodeColor = 'blue' | 'gold' | 'green';

export const QUIET_THRESHOLD_DAYS = 30;

export function statusOf(p: Prayer): PrayerStatus {
  return p.status ?? 'Open';
}

/**
 * Primary display name for a prayer. Applies the user's merge map so
 * "my mom" resolves to "Mom" once merged. The raw parsed value is never
 * modified — merge is a read-time transform, so unmerging restores the
 * raw display without any data migration.
 */
export function whoOf(p: Prayer): string {
  // Delegates to the shared explicit-map helper (CR, PC-B rev 9 decision 6)
  // with the store's active map. Under CR the active map is empty, so this
  // resolves to baseline by construction.
  return whoOfWithMap(p, getWhoMergeMap());
}

/**
 * Raw parsed `who` without merge-map canonicalization. Use this when
 * editing the underlying prayer data (edit form) or serializing to a
 * representation that should reflect what Grok actually returned.
 */
export function rawWhoOf(p: Prayer): string {
  return p.parsed?.who?.trim() || GENERAL_KEY;
}

export function cardSummary(p: Prayer): string {
  let source = p.parsed?.core_prayer?.trim() || p.rawText;
  source = source.replace(/^(?:prayer|praying)\s+(?:for|about|over|regarding)\s+/i, '');
  source = source.replace(/^(?:God,?|Father,?|Lord,?|Heavenly\s+Father,?|Jesus,?|Christ,?)\s*/i, '');
  source = source.replace(/^(?:please\s+)/i, '');
  return source.length > 120 ? source.slice(0, 117).trimEnd() + '…' : source;
}

// Canonical default-summary derivation from a parsed core_prayer, mirroring
// the original PrayPage.handleSave behavior. Strips a leading
// "prayer/praying for|about|over|regarding ". Returns undefined for blank
// input AND blank post-strip output so callers can `?? fallback` and never
// store a blank that render gates treat as missing. This populates
// gardenSummary (the mailed-card "about" line; NOT the share-card noun,
// which is parsed.cardLabel); the enrichment effect uses it and the
// garden_summary backfill SQL mirrors this exact strip.
export function summaryFromCorePrayer(
  corePrayer: string | null | undefined,
): string | undefined {
  if (!corePrayer) return undefined;
  const stripped = corePrayer
    .replace(/^(?:prayer|praying)\s+(?:for|about|over|regarding)\s+/i, '')
    .trim();
  return stripped || undefined;
}

export function prominenceScore(p: Prayer): number {
  const history = p.progressHistory ?? [];
  if (history.length === 0) return 0;

  const createdAt = new Date(p.createdAt).getTime();
  const latest = Math.max(
    new Date(p.updatedAt).getTime(),
    ...history.map((h) => new Date(h.date).getTime())
  );
  const daysSpan = Math.max(0, (latest - createdAt) / (1000 * 60 * 60 * 24));
  return history.length * Math.sqrt(1 + daysSpan / 7);
}

export function tierFor(score: number): Tier {
  if (score >= 4) return 'flourishing';
  if (score >= 1) return 'growing';
  return 'seedling';
}

export function themeOf(p: Prayer): string {
  const core = p.parsed?.core_prayer?.trim();
  if (!core) return '';
  const cleaned = core
    .replace(/^(praying|prayer|pray)\s+(for|about|over|regarding)\s+/i, '')
    .replace(/^(asking|request)\s+(for|about)\s+/i, '');
  const words = cleaned.split(/\s+/).slice(0, 3);
  let theme = words.join(' ');
  theme = theme.replace(/[.,;:!?]+$/, '');
  if (theme.length > 28) theme = theme.slice(0, 25).trimEnd() + '…';
  return theme.charAt(0).toUpperCase() + theme.slice(1);
}

export function colorFor(p: Prayer): NodeColor {
  const status: PrayerStatus = p.status ?? 'Open';
  if (status === 'Answered') return 'green';
  if (status === 'Moving') return 'gold';
  return 'blue';
}

// All formatters omit `hour12` so each locale renders its native time
// convention — 12-hour with AM/PM in English, 24-hour in Spanish and
// Portuguese. Forcing `hour12: true` would produce "4:53 P. M." in
// Spanish, which feels translated.
//
// Each formatter accepts an optional `language` BCP-47 tag. Browser
// callers omit it and get current behavior (formatLocale() reads
// i18n.language live). Server-side callers (Phase B keepsake render
// path) pass it explicitly so output is deterministic without
// touching the global i18n state.

export function formatShortDate(iso: string, language?: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(language ? canonicalLocale(language) : formatLocale(), {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

export function formatShortDateTime(iso: string, language?: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const locale = language ? canonicalLocale(language) : formatLocale();
  const date = d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date}, ${time}`;
}

export function formatUpdateDate(iso: string, language?: string): string {
  const d = new Date(iso);
  const locale = language ? canonicalLocale(language) : formatLocale();
  // Stays in dim chrome (history list rows). Uppercase + middot reads
  // as a label here; sentence-case is reserved for warm message
  // surfaces (the share card meta line).
  const date = d.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  }).toUpperCase();
  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} · ${time}`;
}

export function formatFullDate(iso: string, language?: string): string {
  return new Date(iso).toLocaleDateString(language ? canonicalLocale(language) : formatLocale(), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatFullDateTime(iso: string, language?: string): string {
  const d = new Date(iso);
  const locale = language ? canonicalLocale(language) : formatLocale();
  const date = d.toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${date} · ${time}`;
}

// --- People-aggregation stats (used by Person profile, etc.) ---

export interface MostPrayedFor {
  who: string;
  count: number;
}

export interface LongestJourney {
  who: string;
  months: number;
}

/**
 * Every distinct named person referenced in a single prayer, as display
 * strings (first-seen casing). Folds the primary `who` and all
 * mentionedPeople into one deduped set (case-insensitive). Excludes
 * "General". Used anywhere a prayer should count toward every person it
 * touches — Person profiles, mostPrayedFor, longestJourney, People grid.
 *
 * Returns display strings, not keys; callers that need matching should
 * lowercase/trim themselves.
 */
export function peopleInPrayer(p: Prayer): string[] {
  // Delegates to the shared explicit-map helper (CR, PC-B rev 9 decision 6)
  // with the store's active map — no private membership fork.
  return peopleInPrayerWithMap(p, getWhoMergeMap());
}

export function mostPrayedFor(prayers: Prayer[]): MostPrayedFor | null {
  if (prayers.length === 0) return null;
  // Count each unique person in each prayer once — a prayer that mentions
  // Mom AND Dad contributes +1 to each, not +1 to Mom+Dad as a pair.
  const counts = new Map<string, { who: string; count: number }>();
  for (const p of prayers) {
    for (const name of peopleInPrayer(p)) {
      const key = whoKey(name);
      const existing = counts.get(key);
      if (existing) existing.count++;
      else counts.set(key, { who: name, count: 1 });
    }
  }
  let best: MostPrayedFor | null = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best;
}

export function longestJourney(prayers: Prayer[]): LongestJourney | null {
  if (prayers.length === 0) return null;
  // Earliest time each person appeared in ANY role (primary or mentioned).
  const earliestByKey = new Map<string, { who: string; earliest: number }>();
  for (const p of prayers) {
    const t = new Date(p.createdAt).getTime();
    for (const name of peopleInPrayer(p)) {
      const key = whoKey(name);
      const existing = earliestByKey.get(key);
      if (!existing || t < existing.earliest) {
        earliestByKey.set(key, { who: name, earliest: t });
      }
    }
  }
  const now = Date.now();
  const MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30;
  let best: LongestJourney | null = null;
  for (const { who, earliest } of earliestByKey.values()) {
    const months = Math.floor((now - earliest) / MS_PER_MONTH);
    if (!best || months > best.months) best = { who, months };
  }
  return best;
}

export function totalUpdates(prayers: Prayer[]): number {
  return prayers.reduce((sum, p) => sum + (p.progressHistory?.length ?? 0), 0);
}

export function uniquePeopleCount(prayers: Prayer[]): number {
  const set = new Set<string>();
  for (const p of prayers) {
    for (const name of peopleInPrayer(p)) set.add(whoKey(name));
  }
  return set.size;
}

// --- Evidence Book helpers ---

export function answeredPrayers(prayers: Prayer[]): Prayer[] {
  return prayers.filter((p) => statusOf(p) === 'Answered');
}

export function answerMoment(prayer: Prayer): string {
  return prayer.answeredAt ?? prayer.updatedAt;
}

export function answeredYears(prayers: Prayer[]): number[] {
  const years = new Set<number>();
  for (const p of answeredPrayers(prayers)) {
    years.add(new Date(answerMoment(p)).getFullYear());
  }
  return Array.from(years).sort((a, b) => b - a);
}

export function answeredInYear(prayers: Prayer[], year: number): Prayer[] {
  return answeredPrayers(prayers)
    .filter((p) => new Date(answerMoment(p)).getFullYear() === year)
    .sort(
      (a, b) =>
        new Date(answerMoment(b)).getTime() -
        new Date(answerMoment(a)).getTime(),
    );
}

export function daysCarried(prayer: Prayer): number {
  const start = new Date(prayer.createdAt).getTime();
  const end = new Date(answerMoment(prayer)).getTime();
  return Math.max(1, Math.round((end - start) / 86400000));
}

// --- Per-person helpers (People grid + per-person prayer view) ---

/**
 * Normalized key for matching two `who` strings as the same person.
 * Conservative normalization — the parser does the semantic heavy
 * lifting via existingPeople context; this handles the mechanical
 * cases that don't need an LLM round-trip.
 *
 * Rules applied:
 *   1. Lowercase + trim outer whitespace
 *   2. Strip leading/trailing punctuation commonly stuck on names
 *      ("Mom," → "mom", "'Dad'" → "dad")
 *   3. Collapse internal whitespace runs to a single space
 *   4. Strip leading English possessive pronouns when followed by at
 *      least two more characters ("my mom" → "mom", "our dad" → "dad").
 *      Guarded so a one-word name like "My" or "Our" isn't normalized
 *      to empty.
 *
 * Spanish/Portuguese possessive stripping ("mi mamá", "minha mãe") is
 * left to the parser — doing it here would need per-locale regexes and
 * would risk double-normalizing what Grok already resolved.
 */
export function prayersForWho(prayers: Prayer[], who: string): Prayer[] {
  // General is a first-class who (rule #22) but is structurally excluded
  // from peopleInPrayer per rule #21 so it doesn't leak into person-
  // aggregation contexts (mostPrayedFor / longestJourney / distinctWhos /
  // existingPeopleList all route through peopleInPrayer). For PersonView /
  // PersonKeepsakeSheet / lastPrayedFor and any other surface that asks
  // "what prayers belong to this who," General routes through whoOf
  // directly — the canonical signal — instead of the peopleInPrayer
  // pipeline.
  if (who === 'General') {
    return prayers.filter((p) => whoOf(p) === 'General');
  }
  // Match against canonical forms on both sides so "my mother" (merged
  // into "Mom") under any slot resolves to the same profile.
  const target = whoKey(canonicalizeWho(who.trim()));
  return prayers.filter((p) => {
    const peopleKeys = peopleInPrayer(p).map(whoKey);
    return peopleKeys.includes(target);
  });
}

function latestTouch(p: Prayer): number {
  const history = p.progressHistory ?? [];
  const updated = new Date(p.updatedAt).getTime();
  const created = new Date(p.createdAt).getTime();
  const mostRecentEntry = history.reduce((max, h) => {
    const t = new Date(h.date).getTime();
    return t > max ? t : max;
  }, 0);
  return Math.max(updated, created, mostRecentEntry);
}

/**
 * Most recent activity timestamp across a pre-filtered set of prayers,
 * looking at updatedAt + createdAt + every progressHistory entry's date.
 * Returns null on an empty set. Used by lastPrayedFor (which filters by
 * who first) and by callers that need "latest touch" over a set built
 * via a different filter — e.g. PeopleGrid's synthesized General entry,
 * which can't go through prayersForWho because peopleInPrayer excludes
 * General by design.
 */
export function lastTouchedAt(prayers: Prayer[]): string | null {
  if (prayers.length === 0) return null;
  let bestMs = -Infinity;
  let bestIso: string | null = null;
  for (const p of prayers) {
    const t = latestTouch(p);
    if (t > bestMs) {
      bestMs = t;
      bestIso = new Date(t).toISOString();
    }
  }
  return bestIso;
}

export function lastPrayedFor(prayers: Prayer[], who: string): string | null {
  return lastTouchedAt(prayersForWho(prayers, who));
}

export function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

export function isQuietThread(lastDate: string, thresholdDays = QUIET_THRESHOLD_DAYS): boolean {
  return daysSince(lastDate) > thresholdDays;
}

export function distinctWhos(prayers: Prayer[]): string[] {
  // Preserve first-seen casing for each distinct key; exclude General.
  // Includes mentionedPeople so Dad surfaces as a Person card even when
  // he appears only as a secondary mention in prayers primarily about
  // Mom. peopleInPrayer handles the primary-who + mentioned dedupe.
  const byKey = new Map<string, string>();
  for (const p of prayers) {
    for (const name of peopleInPrayer(p)) {
      const key = whoKey(name);
      if (!byKey.has(key)) byKey.set(key, name);
    }
  }
  return Array.from(byKey.values());
}

/**
 * Top canonical names in the corpus, sorted by how often each person has
 * been prayed for. Fed to the Grok parse prompt as `existingPeople` so
 * Grok can reuse exact forms when a new prayer references someone
 * already named — "praying for my mother" becomes `who: "Mom"` if the
 * user has already been writing about Mom. This is the primary defense
 * against duplicate people profiles.
 *
 * Capped at 50 entries so the prompt token cost stays flat as the
 * corpus grows. Excludes General; excludes Self (Self is a protocol
 * literal, not a person Grok needs to match).
 */
export function existingPeopleList(prayers: Prayer[], limit = 50): string[] {
  const counts = new Map<string, { who: string; count: number }>();
  for (const p of prayers) {
    const seen = new Set<string>();
    for (const name of peopleInPrayer(p)) {
      if (isSelf(name)) continue;
      const key = whoKey(name);
      if (seen.has(key)) continue;
      seen.add(key);
      const existing = counts.get(key);
      if (existing) existing.count++;
      else counts.set(key, { who: name, count: 1 });
    }
  }
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.who.localeCompare(b.who))
    .slice(0, limit)
    .map((e) => e.who);
}

export function mostMatureColor(prayers: Prayer[]): NodeColor {
  let hasMoving = false;
  for (const p of prayers) {
    if (statusOf(p) === 'Answered') return 'green';
    if (colorFor(p) === 'gold') hasMoving = true;
  }
  return hasMoving ? 'gold' : 'blue';
}

/**
 * Find the best alias candidate for `who` from the user's corpus —
 * i.e. another distinct canonical name that plausibly refers to the
 * same person. Returns null when no candidate clears the threshold, or
 * when the best match has already been dismissed (dismissed pair keys
 * are expected to be injected by the caller to keep this helper pure).
 *
 * Returned `candidate` is the canonical form of the other person;
 * `count` is how many prayers that person has (for the suggestion hint
 * copy). Ranking: containment first, then fewest edit distance.
 */
export interface AliasCandidate {
  candidate: string;
  count: number;
}

export function findAliasCandidate(
  who: string,
  prayers: Prayer[],
  dismissedPairKeys: ReadonlySet<string>,
): AliasCandidate | null {
  const targetKey = whoKey(who);
  if (!targetKey) return null;
  const others = distinctWhos(prayers).filter((n) => whoKey(n) !== targetKey);
  let best: { candidate: string; count: number; dist: number; contains: boolean } | null = null;
  for (const candidate of others) {
    // P2: never suggest merging into a reserved sentinel. distinctWhos already
    // excludes General upstream, but NOT Self, and couldBeSamePerson can match
    // a real name against "Self" (e.g. "Sela"). The General skip is defensive.
    if (!isAliasCandidateEligible(candidate)) continue;
    if (!couldBeSamePerson(who, candidate)) continue;
    const pairKey = [whoKey(who), whoKey(candidate)].sort().join('␟');
    if (dismissedPairKeys.has(pairKey)) continue;
    // P3: same containment predicate as couldBeSamePerson, so the
    // "contains-first" rank order can never drift from what actually matched.
    const contains = isContainmentMatch(whoKey(who), whoKey(candidate));
    const dist = levenshtein(whoKey(who), whoKey(candidate));
    if (
      !best ||
      (contains && !best.contains) ||
      (contains === best.contains && dist < best.dist)
    ) {
      best = {
        candidate,
        count: prayersForWho(prayers, candidate).length,
        dist,
        contains,
      };
    }
  }
  return best ? { candidate: best.candidate, count: best.count } : null;
}

/**
 * Format a span between two ISO timestamps using the same buckets as
 * `formatCarrySpan` — "today" / "N days" / "N weeks" / "N months" /
 * "Ny Nm". Pure helper for per-pair callers (e.g. RememberStream's
 * answered-moment renderer needs "this prayer's createdAt → its
 * answered date" rather than the person-wide carry that
 * `formatCarrySpan` computes).
 *
 * Returns '' when end < start (defensive — caller should avoid).
 */
export function formatSpanBetween(
  startIso: string,
  endIso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '';
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startDay = Date.UTC(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  );
  const endDay = Date.UTC(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate(),
  );
  const diffDays = Math.floor((endDay - startDay) / 86400000);
  if (diffDays === 0) return t('people.spanToday');
  if (diffDays < 7) return t('people.spanDays', { count: diffDays });
  if (diffDays < 60) return t('people.spanWeeks', { count: Math.floor(diffDays / 7) });
  const totalMonths = Math.floor(diffDays / 30);
  if (totalMonths < 12) return t('people.spanMonths', { count: totalMonths });
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return months === 0
    ? t('people.spanYears', { count: years })
    : t('people.spanYearsMonths', { years, months });
}

/**
 * Format the carry-span for a set of prayers about one person —
 * "3y 2m" for long journeys, "4 months" for medium, "3 weeks" or
 * "2 days" for recent, "today" for same-day. Readable at a glance in
 * the stats strip; not a clock, a calm span description.
 *
 * Thin wrapper over `formatSpanBetween` that derives earliest /
 * latest from the array — earliest createdAt, latest touch (which
 * includes updatedAt + every progressHistory entry).
 */
export function formatCarrySpan(
  prayers: Prayer[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (prayers.length === 0) return '';
  let earliest = Infinity;
  let latest = -Infinity;
  for (const p of prayers) {
    const created = new Date(p.createdAt).getTime();
    if (created < earliest) earliest = created;
    const touch = latestTouch(p);
    if (touch > latest) latest = touch;
  }
  if (earliest === Infinity || latest === -Infinity) return '';
  return formatSpanBetween(
    new Date(earliest).toISOString(),
    new Date(latest).toISOString(),
    t,
  );
}

export function extractThemes(prayers: Prayer[], limit = 3): string[] {
  const counts = new Map<string, number>();
  for (const p of prayers) {
    const core = p.parsed?.core_prayer;
    if (!core) continue;
    for (const token of tokenize(core)) {
      counts.set(token, (counts.get(token) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([token]) => token);
}

export function formatRelativeTime(
  timestamp: number,
  t: (key: string, options?: Record<string, unknown>) => string,
  nowMs: number = Date.now(),
): string {
  const diffMs = nowMs - timestamp;
  if (!Number.isFinite(diffMs) || diffMs < 0) {
    return t('bible.continueReadingTimeJustNow');
  }
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('bible.continueReadingTimeJustNow');
  if (minutes < 60) return t('bible.continueReadingTimeMinutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('bible.continueReadingTimeHours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days === 1) return t('bible.continueReadingTimeYesterday');
  if (days < 7) return t('bible.continueReadingTimeDays', { count: days });
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return t('bible.continueReadingTimeWeeks', { count: weeks });
  const months = Math.floor(days / 30);
  if (months < 12) return t('bible.continueReadingTimeMonths', { count: months });
  const years = Math.floor(days / 365);
  return t('bible.continueReadingTimeYears', { count: years });
}
